import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@app/reducers';
import {
  CrudService, CrudType,
  CustomDirectiveConfig,
  LoggedInUserModel, ModulesBasedApiSuffix, PERMISSION_RESTRICTION_ERROR,
  ResponseMessageTypes, SnackbarService,
  addDefaultTableObjectProperties,
  currentComponentPageBasedPermissionsSelector$,
  fileUrlDownload,
  onActionSubmittedUtil,
  prepareDynamicTableTabsFromPermissions,
  prepareGetRequestHttpParams
} from '@app/shared';
import { RxjsService } from '@app/shared/services';
import { IApplicationResponse } from '@app/shared/utils/interfaces.utils';
import { MY_TASK_COMPONENT } from '@modules/my-tasks/shared';
import { loggedInUserData } from '@modules/others';
import { SalesModuleApiSuffixModels } from '@modules/sales';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/api';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { PrimeNgTableVariablesModel } from '../../../../../shared/models/prime-ng-table-list-component-variables.model';
@Component({
  selector: 'tasks-sales-doa-dashboard',
  templateUrl: './doa-dashboard.component.html'
})
export class MyTasksSalesDOADashboardComponent extends PrimeNgTableVariablesModel implements OnInit {
  isADecimalOnly = new CustomDirectiveConfig({ isADecimalOnly: true, });
  primengTableConfigProperties: any = {
    tableCaption: "DOA Dashboard",
    shouldShowBreadCrumb: true,
    selectedTabIndex: 0,
    breadCrumbItems: [{ displayName: 'My Tasks' }, { displayName: 'DOA Dashboard' }],
    tableComponentConfigs: {
      tabsList: [
        {
          caption: "DOA Dashboard",
          dataKey: 'doaApprovalId',
          columns: [{ field: 'requestNumber', header: 'Request Nr' },
          { field: 'requestDate', header: 'Req Date', isDateTime: true },
          { field: 'requestor', header: 'Requestor' },
          { field: 'requestorRole', header: 'Req Role' },
          { field: 'requestType', header: 'Req Type' },
          { field: 'customerName', header: 'Customer Name' },
          { field: 'regionName', header: 'Region' },
          { field: 'branchName', header: 'Branch' },
          { field: 'suburbName', header: 'Suburb' },
          { field: 'approvalValue', header: 'Approval Value', isRandSymbolRequired: true ,shouldShowTextField:true,validateInput:this.isADecimalOnly},
          { field: 'leadQuoteRefNo', header: 'Reference', isLink: true }
          ],
          apiSuffixModel: SalesModuleApiSuffixModels.DOA_DASHBOARD,
          moduleName: ModulesBasedApiSuffix.SALES,
        }]
    }
  }

  constructor(
    private router: Router, private snackbarService: SnackbarService,
    private crudService: CrudService,
    private store: Store<AppState>,
    public dialogService: DialogService,
    private rxjsService: RxjsService) {
    super();
    this.primengTableConfigProperties = addDefaultTableObjectProperties(this.primengTableConfigProperties, [{ index: 0, propertyName: 'enableAddActionBtn' }]);
  }

  ngOnInit() {
    this.combineLatestNgrxStoreData();
    this.getDOAConfigs();
  }

  combineLatestNgrxStoreData() {
    combineLatest([
      this.store.select(loggedInUserData), this.store.select(currentComponentPageBasedPermissionsSelector$)]
    ).pipe(distinctUntilChanged()).subscribe((response) => {
      if (!response) return;
      this.loggedInUserData = new LoggedInUserModel(response[0]);
      if (response[1][MY_TASK_COMPONENT.SALES_DOA]) {
        let prepareDynamicTableTabsFromPermissionsObj = prepareDynamicTableTabsFromPermissions(this.primengTableConfigProperties, response[1][MY_TASK_COMPONENT.SALES_DOA]);
        this.primengTableConfigProperties = prepareDynamicTableTabsFromPermissionsObj['primengTableConfigProperties'];
      }
    });
  }

  getDOAConfigs(pageIndex?: string, pageSize?: string, otherParams?: object): void {
    this.loading = true;
    if (!otherParams) {
      otherParams = {};
    }
    otherParams['userId'] = this.loggedInUserData.userId;
    this.crudService.get(
      ModulesBasedApiSuffix.SALES,
      SalesModuleApiSuffixModels.DOA_DASHBOARD_LIST,
      undefined,
      false,
      prepareGetRequestHttpParams(pageIndex, pageSize, otherParams)
    ).subscribe((response: IApplicationResponse) => {
      this.loading = false;
      if (response.isSuccess && response.statusCode === 200) {
        this.dataList = response.resources
        this.totalRecords = response.totalCount;
      }
      this.rxjsService.setGlobalLoaderProperty(false);
    });
  }

  onActionSubmited(e) {
    onActionSubmittedUtil(e, this.onCRUDRequested.bind(this));
  }

  onCRUDRequested(type: CrudType | string, row?: object, otherParams?): void {
    switch (type) {
      case CrudType.GET:
        this.getDOAConfigs(
          row["pageIndex"], row["pageSize"], otherParams);
        break;
      case CrudType.EDIT:
        if (otherParams == "leadQuoteRefNo" && row['reference']) {
          this.openLinkInNewTab(row['reference']);
        }
        else if (otherParams == "leadQuoteRefNo" && !row['reference']) {
          this.snackbarService.openSnackbar('The given link is invalid', ResponseMessageTypes.WARNING);
        }
        break;
      case CrudType.VIEW:
        if (!this.primengTableConfigProperties.tableComponentConfigs.tabsList[0].canEdit) {
          this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
        }
        else if (this.primengTableConfigProperties.tableComponentConfigs.tabsList[0].canEdit) {
          this.router.navigate(['/my-tasks/sales-DOA/detail'], { queryParams: { id: row['doaApprovalId'] } })
        }
        break;
    }
  }

  openLinkInNewTab(link?: string) {
    if (link) {
      this.downloadDocument(link);
    }
  }

  getFileName(filePath: string): string {
    if (!filePath) return 'download';
    const pathOnly = filePath.split('?')[0];
    return pathOnly.split('/').pop() || pathOnly.split('\\').pop() || 'download';
  }

  downloadDocument(documentPath: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (documentPath) {
      const fileName = this.getFileName(documentPath);
      fileUrlDownload(
        documentPath,
        fileName,
        false,
        this.crudService,
        this.rxjsService,
        true,
        'download'
      ).catch(error => {
        console.error('Error opening document:', error);
        this.snackbarService.openSnackbar('Unable to open document', ResponseMessageTypes.ERROR);
      });
    }
  }
}
